import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, ReplaySubject, catchError, map, of, switchMap, take } from 'rxjs';
import seed from '../constants/diffuser-dreams.catalog.json';
import { NavLink } from '../constants/basel';
import { CatalogKind, CatalogProduct, FaqItem, SeedProduct } from '../models/catalog.model';
import { CategoryTree } from '../models/category.model';
import { Product, ProductListItem } from '../models/product.model';
import { isPropsHtml, normalizeCatalogText } from '../utils/catalog-text.util';
import { CategoryService } from './category.service';
import { ProductService } from './product.service';

const SEED = (seed as SeedProduct[]).map(fromSeed);

/** Same page size as HVAC catalog (`CatalogComponent.pageSize`). */
export const CATALOG_PAGE_SIZE = 16;

/** API hard cap per request. */
export const CATALOG_PAGE_SIZE_MAX = 100;

export interface CatalogQuery {
  categorySlug?: string | null;
  /** Direct id when already resolved (brand category filter). */
  categoryId?: string | null;
  brandId?: string | null;
  /** Legacy menu links (`?kind=diffuser|oil`); resolved to a root category when no slug. */
  kind?: CatalogKind | 'all' | null;
  search?: string | null;
  page?: number;
  pageSize?: number;
}

export interface CatalogPage {
  items: CatalogProduct[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export const FAQS: FaqItem[] = [
  {
    question: 'Does an ultrasonic diffuser use heat?',
    answer:
      'The ultrasonic diffuser operates without using heat, making it an adiabatic process. This means that the oil changes states without any heat being involved, keeping the oil intact and preserving its pure plant goodness. This makes ultrasonic diffusers a safer option in households with children when compared to candles.'
  },
  {
    question: 'Can an ultrasonic diffuser be used as a humidifier?',
    answer:
      'Using an ultrasonic diffuser as a humidifier is a great idea as it operates with water. Although adding water to the diffuser can dilute the essential oil and reduce its potency, it is still a very suitable option if you prefer mild fragrances and want the oil to last longer. So, if you are looking for a way to double up your diffuser as a humidifier, go ahead and do it with confidence!'
  },
  {
    question: 'What is an evaporative diffuser and how does it differ from a heat diffuser?',
    answer:
      'When it comes to essential oil diffusers, it’s important to understand the differences between evaporative and heat diffusers. One key distinction is that evaporative diffusers utilize a fan to disperse the oil, whereas heat diffusers rely solely on evaporation. The fan in evaporative diffusers generates wind which allows for more effective oil evaporation.'
  },
  {
    question: 'What is a heat diffuser for essential oils?',
    answer:
      'If you’re looking to use a heat diffuser, it’s important to understand that the process involves heating essential oils until they evaporate. The key to success lies in selecting the right oil for your needs. For example, Sandalwood, a thicker oil, can be a great option as it can easily evaporate into the air when used in a heat diffuser. Heat diffusers that rely on melting oils using a candle or flame usually come with a tray placed next to the heat source. This tray allows the oils to evaporate when they reach a high enough temperature. With the right oil and diffuser, you can create a relaxing and calming atmosphere in no time!'
  },
  {
    question: 'What are the disadvantages of heat diffusers?',
    answer:
      'When you are looking for a cheap and cheerful option, heat diffusers work very well. They disperse the essential oil but because the heat alters the chemistry of the oil it can have an impact on the health benefits of each individual oil. Users must also be aware of the dangers posed by the burning flame in certain environments such as with young children around.'
  }
];

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly productsApi = inject(ProductService);
  private readonly categoriesApi = inject(CategoryService);
  private readonly categoriesReady$ = new ReplaySubject<void>(1);

  readonly loading = signal(true);
  readonly categoryTree = signal<CategoryTree[]>([]);
  readonly faqs = FAQS;

  /** Top-level shop categories + active children for header/footer menus. */
  readonly categoryNav = computed(() => buildCategoryNav(this.categoryTree()));

  constructor() {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.categoriesApi
      .getTree()
      .pipe(catchError(() => of([] as CategoryTree[])))
      .subscribe((categories) => {
        this.categoryTree.set(categories);
        this.loading.set(false);
        this.categoriesReady$.next();
      });
  }

  /** Paged product list from the API (category / brand / search filters applied in the DB). */
  queryProducts(query: CatalogQuery = {}): Observable<CatalogPage> {
    return this.categoriesReady$.pipe(
      take(1),
      switchMap(() => {
        const page = query.page && query.page > 0 ? query.page : 1;
        const pageSize = Math.min(
          Math.max(query.pageSize ?? CATALOG_PAGE_SIZE, 1),
          CATALOG_PAGE_SIZE_MAX
        );
        const categoryId =
          query.categoryId?.trim() ||
          this.resolveCategoryId(query.categorySlug, query.kind);
        const brandId = query.brandId?.trim() || undefined;
        const search = query.search?.trim() || undefined;

        return this.productsApi
          .getPaged({ categoryId, brandId, search, page, pageSize })
          .pipe(
            map((res) => ({
              items: res.items.map((item) => fromListItem(item)),
              totalCount: res.totalCount,
              page: res.page,
              pageSize: res.pageSize
            })),
            catchError(() =>
              of({ items: [] as CatalogProduct[], totalCount: 0, page, pageSize })
            )
          );
      })
    );
  }

  bySlug(slug: string): Observable<CatalogProduct | null> {
    return this.productsApi.getBySlug(slug).pipe(
      map((product) => fromProduct(product)),
      catchError((err) => {
        const status = (err as { status?: number })?.status;
        if (status === 404) {
          return of(null);
        }
        const seedHit = SEED.find((item) => item.slug === slug) ?? null;
        return of(seedHit);
      })
    );
  }

  resolveCategoryId(
    categorySlug?: string | null,
    kind?: CatalogKind | 'all' | null
  ): string | undefined {
    const tree = this.categoryTree();
    const slug = categorySlug?.trim();
    if (slug) {
      for (const parent of tree) {
        if (parent.slug === slug) {
          return parent.id;
        }
        for (const child of parent.children ?? []) {
          if (child.slug === slug) {
            return child.id;
          }
        }
      }
    }

    if (kind === 'diffuser' || kind === 'oil') {
      const match = tree.find((parent) => {
        const hay = `${parent.slug} ${parent.name}`.toLowerCase();
        return kind === 'oil'
          ? hay.includes('oil') || hay.includes('fragrance') || hay.includes('fragance')
          : hay.includes('diffuser');
      });
      return match?.id;
    }

    return undefined;
  }
}

function buildCategoryNav(tree: CategoryTree[]): NavLink[] {
  return tree
    .filter((parent) => parent.isActive)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((parent) => ({
      label: parent.name.toUpperCase(),
      // Same pattern as HVAC: `/catalog/:categorySlug`
      path: `/shop/${parent.slug}`,
      children: (parent.children ?? [])
        .filter((child) => child.isActive && child.id !== parent.id)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map((child) => ({
          label: child.name,
          path: `/shop/${child.slug}`
        }))
    }));
}

function fromSeed(item: SeedProduct): CatalogProduct {
  const images = item.images ?? [];
  return {
    id: item.slug,
    slug: item.slug,
    sku: item.sku,
    name: item.name,
    subtitle: item.categoryName,
    price: item.price,
    compareAtPrice: item.compareAtPrice,
    imageUrl: images[0] ?? '',
    images,
    imageAlt: item.name,
    shortDescription: normalizeCatalogText(item.shortDescription),
    description: normalizeCatalogText(item.description || item.shortDescription || ''),
    kind: item.kind,
    categorySlug: item.categorySlug,
    categoryName: item.categoryName
  };
}

function normalizeHtml(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function resolveDescription(
  apiDescription: string | null | undefined,
  seed?: Pick<CatalogProduct, 'shortDescription' | 'description'>
): string {
  const api = normalizeCatalogText(normalizeHtml(apiDescription));
  const short = normalizeCatalogText(normalizeHtml(seed?.shortDescription));
  const long = normalizeCatalogText(normalizeHtml(seed?.description));
  if (long && !isPropsHtml(long) && (!api || isPropsHtml(api) || api === short || api === long)) {
    return long;
  }
  if (api && api !== short) {
    return api;
  }
  return long || api;
}

function kindFromCategory(
  categorySlug: string | null | undefined,
  categoryName: string | null | undefined,
  fallback?: CatalogKind
): CatalogKind {
  const hay = `${categorySlug ?? ''} ${categoryName ?? ''}`.toLowerCase();
  if (hay.includes('oil') || hay.includes('fragrance') || hay.includes('blend')) {
    return 'oil';
  }
  return fallback ?? 'diffuser';
}

function fromListItem(item: ProductListItem): CatalogProduct {
  const image = item.primaryImageUrl ?? '';
  const seed = SEED.find((row) => row.slug === item.slug);
  return {
    id: item.id,
    slug: item.slug,
    sku: item.sku,
    name: item.name,
    subtitle: item.categoryName ?? seed?.categoryName ?? '',
    price: item.price,
    compareAtPrice: item.compareAtPrice,
    defaultVariantId: item.defaultVariantId ?? null,
    imageUrl: image || seed?.imageUrl || '',
    images: image ? [image] : seed?.images ?? [],
    imageAlt: item.name,
    shortDescription: normalizeCatalogText(seed?.shortDescription),
    description: normalizeCatalogText(seed?.description),
    kind: seed?.kind ?? kindFromCategory(item.categorySlug, item.categoryName),
    categorySlug: item.categorySlug ?? seed?.categorySlug ?? '',
    categoryName: item.categoryName ?? seed?.categoryName ?? '',
    categoryId: item.categoryId
  };
}

function fromProduct(item: Product): CatalogProduct {
  const images = [...(item.images ?? [])].sort((a, b) => a.sortOrder - b.sortOrder).map((img) => img.url);
  const primary = item.images?.find((img) => img.isPrimary)?.url ?? images[0] ?? item.primaryImageUrl ?? '';
  const seed = SEED.find((row) => row.slug === item.slug);
  const relatedProducts = (item.relatedProducts ?? []).map(fromListItem);
  return {
    id: item.id,
    slug: item.slug,
    sku: item.sku,
    name: item.name,
    subtitle: item.categoryName ?? seed?.categoryName ?? '',
    price: item.price,
    compareAtPrice: item.compareAtPrice,
    imageUrl: primary || seed?.imageUrl || '',
    images: images.length ? images : seed?.images ?? [],
    imageAlt: item.name,
    shortDescription: normalizeCatalogText(seed?.shortDescription),
    description: resolveDescription(item.description, seed),
    kind: seed?.kind ?? kindFromCategory(item.categorySlug, item.categoryName),
    categorySlug: item.categorySlug ?? seed?.categorySlug ?? '',
    categoryName: item.categoryName ?? seed?.categoryName ?? '',
    categoryId: item.categoryId,
    relatedProducts,
    options: item.options ?? [],
    variants: item.variants ?? []
  };
}
